import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GAMES, GameId, GameMeta,
  getHighScore, getScore, totalPlays,
  getLeaderboard, LeaderRow,
  getFriends, addFriend, removeFriend, logFriendScore, markInvited, Friend, FriendKind, FRIEND_KINDS,
  buildChallengeUrl, readChallengeFromUrl, Challenge,
  getPlayer, onGamesChanged, reconcileWithServer,
  getDailyStatus, getGame,
  getStats, getAchievementsWithStatus, getEarnedAchievementIds, checkAchievements, Achievement, ALL_ACHIEVEMENTS,
} from './gamesLib';
import { isMuted, toggleMuted } from './gameSound';
import { hasGamesApi, fetchGlobalLeaderboard, GlobalLeaderRow } from './gamesApi';
import Game2048 from './games/Game2048';
import GameSnake from './games/GameSnake';
import GameMemory from './games/GameMemory';
import GameTicTacToe from './games/GameTicTacToe';
import GameMinesweeper from './games/GameMinesweeper';
import GameTetris from './games/GameTetris';
import GameBreakout from './games/GameBreakout';
import GameSudoku from './games/GameSudoku';
import GameConnect4 from './games/GameConnect4';
import GamePong from './games/GamePong';
import GameSimon from './games/GameSimon';
import GameSolitaire from './games/GameSolitaire';
import './GamesPage.css';

type View = 'arcade' | 'leaderboard' | 'stats';

const GAME_COMPONENTS: Record<GameId, React.FC<{ onExit: () => void; challengeScore?: number | null }>> = {
  '2048': Game2048,
  snake: GameSnake,
  memory: GameMemory,
  tictactoe: GameTicTacToe,
  minesweeper: GameMinesweeper,
  tetris: GameTetris,
  breakout: GameBreakout,
  sudoku: GameSudoku,
  connect4: GameConnect4,
  pong: GamePong,
  simon: GameSimon,
  solitaire: GameSolitaire,
};

const GamesPage: React.FC<{ guestMode?: boolean }> = ({ guestMode = false }) => {
  const [view, setView] = useState<View>('arcade');
  const [active, setActive] = useState<GameId | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [muted, setMuted] = useState(isMuted());
  const [toasts, setToasts] = useState<Achievement[]>([]);
  const [, force] = useState(0);
  const earnedRef = useRef<Set<string>>(new Set(getEarnedAchievementIds()));

  // Re-render on any games-data change (scores, friends, etc.) and surface any
  // newly-earned achievements as toasts.
  useEffect(() => {
    checkAchievements();
    return onGamesChanged(() => {
      force(n => n + 1);
      const now = getEarnedAchievementIds();
      const fresh = now.filter(id => !earnedRef.current.has(id));
      if (fresh.length) {
        earnedRef.current = new Set(now);
        const badges = fresh.map(id => ALL_ACHIEVEMENTS.find(a => a.id === id)!).filter(Boolean);
        setToasts(t => [...t, ...badges]);
        badges.forEach(b => setTimeout(() => setToasts(t => t.filter(x => x.id !== b.id)), 5000));
      }
    });
  }, []);

  // Pick up a challenge link on mount (the viral, attract-non-members loop).
  useEffect(() => {
    const c = readChallengeFromUrl();
    if (c) {
      setChallenge(c);
      setActive(c.g);
    }
  }, []);

  // For logged-in members: reconcile scores/friends with PostgreSQL on entry so
  // the DB is the source of truth (localStorage is just an offline cache).
  useEffect(() => {
    reconcileWithServer().then(ok => { if (ok) force(n => n + 1); });
  }, []);

  const player = getPlayer();

  const toggleSound = () => setMuted(toggleMuted());

  if (active) {
    const Comp = GAME_COMPONENTS[active];
    const challengeScore = challenge && challenge.g === active ? challenge.s : null;
    const beaten = !!challenge && challenge.g === active && getHighScore(active) >= challenge.s;
    return (
      <div className="games-page games-play">
        {challenge && challenge.g === active && !beaten && (
          <div className="games-challenge-banner">
            <span>🥊 <b>{challenge.n}</b> challenged you on <b>{GAMES.find(g => g.id === active)?.title}</b> — beat <b>{challenge.s}</b>!</span>
            <button onClick={() => setChallenge(null)} aria-label="Dismiss">✕</button>
          </div>
        )}
        {challenge && challenge.g === active && beaten && (
          <div className="games-beaten-banner">
            🎉 You beat <b>{challenge.n}</b>’s score of {challenge.s}!{' '}
            {guestMode
              ? <a className="games-cta-link" href="/auth">Create a free Akountant account to claim your crown →</a>
              : <span>Send them a fresh challenge from the leaderboard.</span>}
          </div>
        )}
        <Comp onExit={() => { setActive(null); setChallenge(null); }} challengeScore={challengeScore} />
        <AchievementToasts toasts={toasts} />
      </div>
    );
  }

  return (
    <div className="games-page">
      {guestMode && (
        <div className="games-guest-cta">
          <span>👋 You’re playing as a guest. Scores save to this browser only.</span>
          <a className="games-cta-btn" href="/auth">Sign up free</a>
        </div>
      )}
      <Landing />

      <div className="games-tabs">
        <button className={view === 'arcade' ? 'active' : ''} onClick={() => setView('arcade')}>🕹️ Arcade</button>
        <button className={view === 'leaderboard' ? 'active' : ''} onClick={() => setView('leaderboard')}>🏆 Leaderboard &amp; Friends</button>
        <button className={view === 'stats' ? 'active' : ''} onClick={() => setView('stats')}>📊 Stats &amp; Badges</button>
        <button className="games-mute" onClick={toggleSound} title={muted ? 'Sound off — click to enable' : 'Sound on — click to mute'}>
          {muted ? '🔇' : '🔊'}
        </button>
      </div>

      {view === 'arcade' && <Arcade onPlay={(id) => { setChallenge(null); setActive(id); }} player={player} onShowStats={() => setView('stats')} />}
      {view === 'leaderboard' && <Leaderboard />}
      {view === 'stats' && <StatsView />}

      <AchievementToasts toasts={toasts} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Achievement toasts
// ---------------------------------------------------------------------------
const AchievementToasts: React.FC<{ toasts: Achievement[] }> = ({ toasts }) => {
  if (toasts.length === 0) return null;
  return (
    <div className="ach-toasts">
      {toasts.map(t => (
        <div key={t.id} className="ach-toast">
          <span className="ach-toast-emoji">{t.emoji}</span>
          <div>
            <div className="ach-toast-title">Achievement unlocked!</div>
            <div className="ach-toast-name">{t.title} — {t.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Witty landing banner
// ---------------------------------------------------------------------------
const Landing: React.FC = () => {
  const plays = totalPlays();
  return (
    <div className="games-hero">
      <div className="games-hero-text">
        <h1>🎮 The Akountant Arcade</h1>
        <p className="games-hero-lead">
          Documents and games don’t exactly go together… <em>but</em>, umm — take a break, play a game.
          Your efficiency may just get a little boost afterward.
        </p>
        <p className="games-hero-sub">
          Work smarter, not harder. Don’t be a slave to your documents. We’ve lined up a few timeless classics —
          slide, slither, flip, flag and outsmart. <b>Score, level up, and challenge your family, friends, clients
          and peers</b> — even the ones who aren’t on Akountant yet. 😉
        </p>
      </div>
      <div className="games-hero-badge">
        <div className="ghb-num">{plays}</div>
        <div className="ghb-label">games played</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Arcade grid
// ---------------------------------------------------------------------------
const Arcade: React.FC<{ onPlay: (id: GameId) => void; player: ReturnType<typeof getPlayer>; onShowStats: () => void }> = ({ onPlay, player, onShowStats }) => {
  return (
    <>
      <DailyCard onPlay={onPlay} />
      <div className="games-grid">
        {GAMES.map(g => <GameCard key={g.id} game={g} onPlay={() => onPlay(g.id)} />)}
        <div className="game-card more-soon">
          <div className="gc-emoji">✨</div>
          <h3>More classics soon</h3>
          <p>Word puzzles, Mahjong, Checkers &amp; more are warming up.</p>
        </div>
      </div>
      <p className="games-foot-note">
        Signed in as <b>{player.name}</b>{player.isMember ? '' : ' (guest)'} · scores save to this device · <button className="games-link" onClick={onShowStats}>see your stats &amp; badges</button>. Beat a friend’s
        challenge and we’ll nudge them to join. 🪝
      </p>
    </>
  );
};

// ---------------------------------------------------------------------------
// Daily challenge card
// ---------------------------------------------------------------------------
const DailyCard: React.FC<{ onPlay: (id: GameId) => void }> = ({ onPlay }) => {
  const daily = getDailyStatus();
  const game = getGame(daily.gameId);
  if (!game) return null;
  return (
    <div className="daily-card" style={{ ['--gc-accent' as any]: game.color }}>
      <div className="daily-left">
        <div className="daily-tag">🗓️ Daily Challenge</div>
        <div className="daily-game"><span className="daily-emoji">{game.emoji}</span> {game.title}</div>
        <div className="daily-sub">{daily.doneToday ? 'Done for today — nice. Come back tomorrow!' : 'A new game every day. Keep your streak alive.'}</div>
      </div>
      <div className="daily-right">
        <div className="daily-streak">
          <span className="daily-streak-num">🔥 {daily.streak}</span>
          <span className="daily-streak-label">day streak</span>
        </div>
        <button className="gc-play" onClick={() => onPlay(daily.gameId)} style={{ background: game.color }}>
          {daily.doneToday ? 'Play again' : 'Play today’s'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stats & Badges
// ---------------------------------------------------------------------------
const StatsView: React.FC = () => {
  const stats = getStats();
  const badges = getAchievementsWithStatus();
  return (
    <div className="games-stats">
      <div className="stats-tiles">
        <div className="stat-tile"><div className="stat-num">{stats.totalPlays}</div><div className="stat-lbl">games played</div></div>
        <div className="stat-tile"><div className="stat-num">{stats.distinctPlayed}/{GAMES.length}</div><div className="stat-lbl">games tried</div></div>
        <div className="stat-tile"><div className="stat-num">🔥 {stats.dailyStreak}</div><div className="stat-lbl">daily streak</div></div>
        <div className="stat-tile"><div className="stat-num">{stats.achievements.earned}/{stats.achievements.total}</div><div className="stat-lbl">badges</div></div>
        <div className="stat-tile"><div className="stat-num">{stats.favorite ? stats.favorite.emoji : '—'}</div><div className="stat-lbl">{stats.favorite ? `favorite: ${stats.favorite.title}` : 'no favorite yet'}</div></div>
      </div>

      <h3 className="stats-h">🏅 Badges</h3>
      <div className="badge-grid">
        {badges.map(b => (
          <div key={b.id} className={`badge ${b.earned ? 'earned' : 'locked'}`} title={b.desc}>
            <div className="badge-emoji">{b.earned ? b.emoji : '🔒'}</div>
            <div className="badge-title">{b.title}</div>
            <div className="badge-desc">{b.desc}</div>
          </div>
        ))}
      </div>

      <h3 className="stats-h">📈 Per-game bests</h3>
      <div className="stats-game-table">
        {GAMES.map(g => {
          const s = getScore(g.id);
          return (
            <div key={g.id} className="sgt-row">
              <span className="sgt-emoji">{g.emoji}</span>
              <span className="sgt-name">{g.title}</span>
              <span className="sgt-best">{s.best} <i>{g.scoreLabel}</i></span>
              <span className="sgt-plays">{s.plays} {s.plays === 1 ? 'play' : 'plays'}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GameCard: React.FC<{ game: GameMeta; onPlay: () => void }> = ({ game, onPlay }) => {
  const score = getScore(game.id);
  const board = getLeaderboard(game.id);
  const leader = board[0];
  return (
    <div className="game-card" style={{ ['--gc-accent' as any]: game.color }}>
      <div className="gc-top">
        <div className="gc-emoji">{game.emoji}</div>
        <div className="gc-titles">
          <h3>{game.title}</h3>
          <p>{game.tagline}</p>
        </div>
      </div>
      <p className="gc-how">{game.how}</p>
      <div className="gc-meta">
        <span className="gc-chip">🏅 Best: <b>{getHighScore(game.id)}</b></span>
        <span className="gc-chip">🎯 {game.levels} levels</span>
        {score.plays > 0 && <span className="gc-chip">🔁 {score.plays} plays</span>}
      </div>
      {leader && (
        <div className="gc-leader">👑 Leading: <b>{leader.name}</b> · {leader.best} {game.scoreLabel}</div>
      )}
      <button className="gc-play" onClick={onPlay}>▶ Play</button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Leaderboard + Friends
// ---------------------------------------------------------------------------
const Leaderboard: React.FC = () => {
  const [gameId, setGameId] = useState<GameId>('2048');
  const game = GAMES.find(g => g.id === gameId)!;
  const rows = getLeaderboard(gameId);
  const friends = getFriends();
  const myBest = getHighScore(gameId);
  const player = getPlayer();

  // Local "circle" vs the GLOBAL members leaderboard (server-backed).
  const [scope, setScope] = useState<'circle' | 'global'>('circle');
  const [globalRows, setGlobalRows] = useState<GlobalLeaderRow[] | null>(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const canGlobal = hasGamesApi();
  useEffect(() => {
    if (scope !== 'global' || !canGlobal) return;
    let alive = true;
    setGlobalLoading(true);
    fetchGlobalLeaderboard(gameId).then(r => { if (alive) { setGlobalRows(r); setGlobalLoading(false); } });
    return () => { alive = false; };
  }, [scope, gameId, canGlobal]);

  // Add-friend form
  const [fname, setFname] = useState('');
  const [fkind, setFkind] = useState<FriendKind>('friend');

  // Log a peer's score
  const [logFor, setLogFor] = useState<string>('');
  const [logVal, setLogVal] = useState('');

  // Challenge link
  const [copied, setCopied] = useState(false);
  const challengeUrl = useMemo(
    () => buildChallengeUrl({ g: gameId, s: myBest, n: player.name }),
    [gameId, myBest, player.name]
  );

  const onAddFriend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fname.trim()) return;
    addFriend(fname, fkind);
    setFname('');
  };

  const onLog = (e: React.FormEvent) => {
    e.preventDefault();
    const v = parseInt(logVal, 10);
    if (!logFor || isNaN(v) || v < 0) return;
    logFriendScore(gameId, logFor, v);
    setLogVal('');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(challengeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="games-leaderboard">
      <div className="lb-game-switch">
        {GAMES.map(g => (
          <button key={g.id} className={gameId === g.id ? 'active' : ''} onClick={() => setGameId(g.id)}
            style={gameId === g.id ? { ['--gc-accent' as any]: g.color, background: g.color, borderColor: g.color } : undefined}>
            {g.emoji} {g.title}
          </button>
        ))}
      </div>

      <div className="lb-grid">
        {/* Ranked table */}
        <div className="lb-panel">
          <div className="lb-panel-head">
            <h3>🏆 {game.title} — standings</h3>
            {canGlobal && (
              <div className="lb-scope-switch">
                <button className={scope === 'circle' ? 'active' : ''} onClick={() => setScope('circle')}>👥 My circle</button>
                <button className={scope === 'global' ? 'active' : ''} onClick={() => setScope('global')}>🌍 Members</button>
              </div>
            )}
          </div>

          {scope === 'global' ? (
            globalLoading ? (
              <p className="lb-empty">Loading the global leaderboard…</p>
            ) : !globalRows || globalRows.length === 0 ? (
              <p className="lb-empty">No member scores yet for {game.title}. Be the first on the board!</p>
            ) : (
              <table className="lb-table">
                <thead><tr><th>#</th><th>Member</th><th>{game.scoreLabel}</th></tr></thead>
                <tbody>
                  {globalRows.map((r, i) => (
                    <tr key={r.user_id} className={r.isYou ? 'you' : ''}>
                      <td className="lb-rank">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</td>
                      <td>{r.name}{r.isYou && <span className="lb-tag-you">you</span>}</td>
                      <td className="lb-score">{r.best}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : rows.length === 0 ? (
            <p className="lb-empty">No scores yet. Play a round, then log your friends’ scores to compete.</p>
          ) : (
            <table className="lb-table">
              <thead><tr><th>#</th><th>Player</th><th>{game.scoreLabel}</th></tr></thead>
              <tbody>
                {rows.map((r: LeaderRow) => (
                  <tr key={r.key} className={r.isYou ? 'you' : ''}>
                    <td className="lb-rank">{r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : r.rank}</td>
                    <td>
                      {r.name}{r.isYou && <span className="lb-tag-you">you</span>}
                      {!r.isMember && <span className="lb-tag-guest">non-member</span>}
                    </td>
                    <td className="lb-score">{r.best}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Log a peer's score */}
          {friends.length > 0 && (
            <form className="lb-logform" onSubmit={onLog}>
              <span className="lb-logform-label">Log a score:</span>
              <select value={logFor} onChange={e => setLogFor(e.target.value)}>
                <option value="">Pick a peer…</option>
                {friends.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <input type="number" min={0} placeholder={game.scoreLabel} value={logVal}
                onChange={e => setLogVal(e.target.value)} />
              <button type="submit" className="g-btn small">Add</button>
            </form>
          )}
        </div>

        {/* Friends + challenge */}
        <div className="lb-panel">
          <h3>🤝 Your circle</h3>
          <p className="lb-sub">Add family, friends, clients or peers — they don’t need an Akountant account to compete.</p>

          <form className="lb-addfriend" onSubmit={onAddFriend}>
            <input type="text" placeholder="Name" value={fname} onChange={e => setFname(e.target.value)} maxLength={40} />
            <select value={fkind} onChange={e => setFkind(e.target.value as FriendKind)}>
              {FRIEND_KINDS.map(k => <option key={k.value} value={k.value}>{k.emoji} {k.label}</option>)}
            </select>
            <button type="submit" className="g-btn small">+ Add</button>
          </form>

          <div className="lb-friends">
            {friends.length === 0 && <p className="lb-empty">No one here yet. Add your first rival above.</p>}
            {friends.map((f: Friend) => {
              const kind = FRIEND_KINDS.find(k => k.value === f.kind);
              return (
                <div key={f.id} className="lb-friend">
                  <span className="lb-friend-kind" title={kind?.label}>{kind?.emoji}</span>
                  <span className="lb-friend-name">{f.name}</span>
                  {f.invited && <span className="lb-invited">invited ✓</span>}
                  <button className="lb-friend-del" onClick={() => removeFriend(f.id)} aria-label="Remove">✕</button>
                </div>
              );
            })}
          </div>

          {/* Challenge link — the attract-non-members hook */}
          <div className="lb-challenge">
            <h4>📣 Challenge a non-member</h4>
            <p className="lb-sub">
              Share this link to <b>{game.title}</b> with your best of <b>{myBest}</b>. They play in their browser —
              beat you, and we invite them to join Akountant to claim the crown.
            </p>
            <div className="lb-challenge-row">
              <input readOnly value={challengeUrl} onFocus={e => e.currentTarget.select()} />
              <button className="g-btn small primary" onClick={copyLink}>{copied ? 'Copied!' : 'Copy link'}</button>
            </div>
            {friends.length > 0 && (
              <div className="lb-challenge-mark">
                Mark as invited:
                {friends.filter(f => !f.invited).map(f => (
                  <button key={f.id} className="lb-mark-chip" onClick={() => markInvited(f.id)}>{f.name}</button>
                ))}
                {friends.every(f => f.invited) && <span className="lb-sub"> all invited 🎉</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamesPage;
